import { Link } from 'react-router-dom'
import { Server, Database, GitBranch, Share2, Check, ArrowRight, Zap, Smartphone, Megaphone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { BadgeStrip } from '@/components/BadgeStrip'
import { planLimitVars, usePlanLimits } from '@/hooks/usePlanLimits'

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.toolbox.m.mobile_api_tester'

// ── Hero ──────────────────────────────────────────────────────────────────

function Hero() {
  const { t } = useTranslation()
  return (
    <section className="relative overflow-hidden bg-background pt-20 pb-24 px-6">
      <div className="mx-auto max-w-4xl text-center space-y-8">
        <Badge variant="secondary" className="text-xs px-3 py-1">
          <Zap className="mr-1 h-3 w-3" />
          {t('landing.badge')}
        </Badge>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          {t('landing.heroTitle1')}
          <br className="hidden sm:block" />
          <span className="text-primary">{t('landing.heroTitle2')}</span>
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          {t('landing.heroSubtitle')}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button size="lg" asChild>
            <Link to="/signup">
              {t('landing.startFree')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/login">{t('landing.login')}</Link>
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t('landing.noCreditCard')}</p>

        {/* 共有ギャラリー導線: 会員登録なしで実際の Mock を試せる最短ルート */}
        <div className="pt-2">
          <a
            href="/gallery/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent"
          >
            <Megaphone className="h-4 w-4 text-primary" />
            {t('landing.galleryCtaButton')}
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
          <p className="mt-2 text-xs text-muted-foreground">{t('landing.galleryCtaSubtitle')}</p>
        </div>
      </div>
    </section>
  )
}

// ── Features ──────────────────────────────────────────────────────────────

function Features() {
  const { t } = useTranslation()

  const features = [
    {
      icon: Database,
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc'),
      badge: t('landing.feature1Badge'),
    },
    {
      icon: GitBranch,
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc'),
      badge: t('landing.feature2Badge'),
    },
    {
      icon: Share2,
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc'),
      badge: t('landing.feature3Badge'),
    },
  ]

  return (
    <section className="py-20 px-6 bg-muted/30">
      <div className="mx-auto max-w-5xl space-y-12">
        <div className="text-center space-y-3">
          <h2 className="text-3xl font-bold tracking-tight">{t('landing.featuresTitle')}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            {t('landing.featuresSubtitle')}
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-3">
          {features.map(({ icon: Icon, title, description, badge }) => (
            <div key={title} className="rounded-xl border bg-background p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="rounded-md bg-primary/10 p-2">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold">{title}</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
              <Badge variant="secondary" className="text-xs">{badge}</Badge>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── How it works ──────────────────────────────────────────────────────────

function HowItWorks() {
  const { t } = useTranslation()

  const steps = [
    { step: '1', title: t('landing.step1Title'), desc: t('landing.step1Desc') },
    { step: '2', title: t('landing.step2Title'), desc: t('landing.step2Desc') },
    { step: '3', title: t('landing.step3Title'), desc: t('landing.step3Desc') },
    { step: '4', title: t('landing.step4Title'), desc: t('landing.step4Desc') },
  ]

  return (
    <section className="py-20 px-6">
      <div className="mx-auto max-w-4xl space-y-12">
        <div className="text-center space-y-3">
          <h2 className="text-3xl font-bold tracking-tight">{t('landing.howItWorksTitle')}</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map(({ step, title, desc }) => (
            <div key={step} className="space-y-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                {step}
              </div>
              <h3 className="font-semibold">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Mobile App ────────────────────────────────────────────────────────────

function MobileApp() {
  const { t } = useTranslation()

  const features = [
    t('landing.mobileFeat1'),
    t('landing.mobileFeat2'),
    t('landing.mobileFeat3'),
  ]

  return (
    <section className="py-20 px-6">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-2xl border bg-muted/30 overflow-hidden">
          <div className="grid sm:grid-cols-2 gap-0">
            {/* Left: text */}
            <div className="p-8 sm:p-12 space-y-6 flex flex-col justify-center">
              <Badge variant="secondary" className="w-fit text-xs">
                <Smartphone className="mr-1 h-3 w-3" />
                {t('landing.mobileComplementLabel')}
              </Badge>
              <div className="space-y-3">
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight leading-snug">
                  {t('landing.mobileTitle')}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t('landing.mobileSubtitle')}
                </p>
              </div>
              <ul className="space-y-2">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-black text-white rounded-xl px-5 py-3 w-fit hover:bg-zinc-800 transition-colors"
              >
                {/* Google Play ロゴ (SVG) */}
                <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="currentColor">
                  <path d="M3.18 23.76c.3.17.64.24.98.2l13.2-11.96L13.7 8.34 3.18 23.76zM.56 1.1C.21 1.49 0 2.06 0 2.82v18.36c0 .76.21 1.33.56 1.72l.09.08 10.27-10.27v-.24L.65 1.02.56 1.1zM22.15 10.5l-2.93-1.67-3.29 2.97 3.29 2.97 2.95-1.68c.84-.48.84-1.11-.02-1.59zM4.16.24l13.2 11.96-3.66 3.66L.98.44C1.32.4 1.66.47 1.96.64L4.16.24z"/>
                </svg>
                <div className="text-left leading-none">
                  <div className="text-[10px] opacity-80">{t('landing.mobileGetApp')}</div>
                  <div className="text-base font-semibold">{t('landing.mobileAppName')}</div>
                </div>
              </a>
            </div>

            {/* Right: app description card */}
            <div className="bg-background border-l p-8 sm:p-12 flex flex-col justify-center space-y-6">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-3">
                  <Smartphone className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-sm leading-tight">{t('landing.mobileAppName')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Android</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('landing.mobileAppDesc')}
              </p>
              {/* Simulated request/response UI */}
              <div className="rounded-lg border bg-muted/50 p-4 font-mono text-xs space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-green-600 font-bold">GET</span>
                  <span className="text-muted-foreground truncate">https://my-api.scenariomock.com/users/1</span>
                </div>
                <div className="border-t pt-2 text-muted-foreground">
                  <span className="text-green-500">200 OK</span>
                  {'  '}
                  <span>{'{ "id": 1, "name": "Yoshi" }'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Pricing ───────────────────────────────────────────────────────────────

function Pricing() {
  const { t } = useTranslation()
  const limitVars = planLimitVars(usePlanLimits())

  const freeFeatures = [
    t('landing.freeFeat1', limitVars),
    t('landing.freeFeat2', limitVars),
    t('landing.freeFeat3', limitVars),
    t('landing.freeFeat4', limitVars),
  ]

  const proFeatures = [
    t('landing.proFeat1'),
    t('landing.proFeat2'),
    t('landing.proFeat3', limitVars),
    t('landing.proFeat4', limitVars),
    t('landing.proFeat5'),
  ]

  return (
    <section className="py-20 px-6 bg-muted/30">
      <div className="mx-auto max-w-4xl space-y-12">
        <div className="text-center space-y-3">
          <h2 className="text-3xl font-bold tracking-tight">{t('landing.pricingTitle')}</h2>
          <p className="text-muted-foreground">{t('landing.pricingSubtitle')}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 max-w-2xl mx-auto">
          {/* Free */}
          <div className="rounded-xl border bg-background p-6 space-y-6">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('landing.free')}</p>
              <p className="text-4xl font-bold">$0<span className="text-base font-normal text-muted-foreground">{t('landing.perMonth')}</span></p>
              <p className="text-sm text-muted-foreground">{t('landing.noCreditCard')}</p>
            </div>
            <Button className="w-full" variant="outline" asChild>
              <Link to="/signup">{t('landing.getStarted')}</Link>
            </Button>
            <ul className="space-y-2">
              {freeFeatures.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div className="rounded-xl border-2 border-primary bg-background p-6 space-y-6 relative">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs">{t('landing.pro')}</Badge>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('landing.pro')}</p>
              <p className="text-4xl font-bold">$9<span className="text-base font-normal text-muted-foreground">{t('landing.perMonth')}</span></p>
            </div>
            <Button className="w-full" asChild>
              <Link to="/signup">{t('landing.getStarted')}</Link>
            </Button>
            <ul className="space-y-2">
              {proFeatures.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

// ── CTA ───────────────────────────────────────────────────────────────────

function CtaBanner() {
  const { t } = useTranslation()
  return (
    <section className="py-20 px-6">
      <div className="mx-auto max-w-2xl text-center space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">{t('landing.ctaTitle')}</h2>
        <p className="text-muted-foreground">
          {t('landing.ctaSubtitle')}
        </p>
        <Button size="lg" asChild>
          <Link to="/signup">
            {t('landing.ctaButton')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </section>
  )
}

// ── Footer ────────────────────────────────────────────────────────────────

function Footer() {
  const { t } = useTranslation()
  return (
    <footer className="border-t pt-8 pb-8 px-6 space-y-6">
      <BadgeStrip />
      <Separator />
      <div className="mx-auto max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          ScenarioMock
        </div>
        <nav className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap justify-center">
          <Link to="/login" className="hover:text-foreground transition-colors">{t('landing.login')}</Link>
          <Link to="/docs" className="hover:text-foreground transition-colors">{t('landing.footerDocs')}</Link>
          {/* 素の <a> を使う: ブログは静的HTML生成のページで、React Router の <Link> だと
              クライアントサイドルーティングに横取りされ SPA の 404 画面になる。
              トップの静的HTMLは英語で書き出すため、言語で切り替えず両方並べる(検索エンジンが両方のブログをたどれるように) */}
          <a href="/blog/" hrefLang="en" className="hover:text-foreground transition-colors">{t('landing.footerBlogEn')}</a>
          <a href="/ja/blog/" hrefLang="ja" className="hover:text-foreground transition-colors">{t('landing.footerBlogJa')}</a>
          <Link to="/terms" className="hover:text-foreground transition-colors">{t('landing.footerTerms')}</Link>
          <Link to="/privacy" className="hover:text-foreground transition-colors">{t('landing.footerPrivacy')}</Link>
          <Link to="/legal/commerce" className="hover:text-foreground transition-colors">{t('landing.footerCommerce')}</Link>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors flex items-center gap-1"
          >
            <Smartphone className="h-3 w-3" />
            Android App
          </a>
        </nav>
        <p className="text-xs text-muted-foreground">© 2026 ScenarioMock</p>
      </div>
    </footer>
  )
}

// ── Header ────────────────────────────────────────────────────────────────

function Header() {
  const { t, i18n } = useTranslation()
  const toggleLang = () => {
    const next = i18n.language === 'ja' ? 'en' : 'ja'
    void i18n.changeLanguage(next)
    localStorage.setItem('i18n-lang', next)
  }
  return (
    <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-sm">
      <div className="mx-auto max-w-5xl flex h-14 items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
          <Server className="h-4 w-4 text-primary" />
          ScenarioMock
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={toggleLang} className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2">
            {t('legal.switchLang')}
          </button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/login">{t('landing.login')}</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to="/signup">{t('landing.startFree')}</Link>
          </Button>
        </div>
      </div>
    </header>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────

export function LandingPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Hero />
        <Separator />
        <Features />
        <Separator />
        <HowItWorks />
        <Separator />
        <MobileApp />
        <Separator />
        <Pricing />
        <Separator />
        <CtaBanner />
      </main>
      <Footer />
    </div>
  )
}
